/**
 * SMSNotificationBell Component
 * Shows unread SMS count in the header with a dropdown of recent messages
 */

import { useState, useRef, useEffect } from 'react'
import { MessageSquare, FileText, User, Leaf } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import { supabase } from '../lib/supabase'
import { getUnreadCount, subscribeToMessages } from '../services/sms'
import type { Communication } from '../types/communication'

export function SMSNotificationBell() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Fetch total unread count
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['sms-unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 30000 // Refresh every 30 seconds
  })

  // Fetch recent unread messages
  const { data: recentMessages = [] } = useQuery({
    queryKey: ['sms-recent-unread'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communications')
        .select('*')
        .eq('direction', 'inbound')
        .is('read_at', null)
        .order('created_at', { ascending: false })
        .limit(5)

      if (error) throw error
      return data as Communication[]
    },
    refetchInterval: 30000
  })

  // Subscribe to real-time updates
  useEffect(() => {
    const unsubscribe = subscribeToMessages(() => {
      // Refetch counts when new message arrives
      queryClient.invalidateQueries({ queryKey: ['sms-unread-count'] })
      queryClient.invalidateQueries({ queryKey: ['sms-recent-unread'] })
    })

    return () => unsubscribe()
  }, [queryClient])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleMessageClick = (message: Communication, goToLinked: 'assignment' | 'recipient' | 'sms' = 'sms') => {
    setIsOpen(false)

    if (goToLinked === 'assignment' && message.assignment_id) {
      // Navigate to the linked assignment
      navigate(`/admin/assignments/${message.assignment_id}`)
    } else if (goToLinked === 'recipient') {
      // Navigate to customer or subcontractor
      if (message.recipient_type === 'customer') {
        navigate(`/admin/customers/${message.recipient_id}`)
      } else {
        navigate(`/admin/subcontractors/${message.recipient_id}`)
      }
    } else {
      // Default: Navigate to SMS page with this conversation
      navigate(`/admin/communications?recipient=${message.recipient_type}&id=${message.recipient_id}`)
    }
  }

  const handleViewAll = () => {
    setIsOpen(false)
    navigate('/admin/communications')
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* SMS Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-nature-600 hover:text-nature-900 dark:text-nature-400 dark:hover:text-nature-100 hover:bg-nature-50 dark:hover:bg-nature-700 rounded-md transition-colors"
        title="SMS-meddelanden"
      >
        <MessageSquare className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="fixed left-1/2 -translate-x-1/2 top-16 w-[calc(100vw-2rem)] max-w-80 md:absolute md:left-auto md:translate-x-0 md:right-0 md:top-auto md:mt-2 md:w-80 bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-nature-900 dark:text-nature-100">
                SMS {unreadCount > 0 && `(${unreadCount} olästa)`}
              </h3>
              <button
                onClick={handleViewAll}
                className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400"
              >
                Visa alla
              </button>
            </div>
          </div>

          {/* Messages List */}
          <div className="max-h-80 overflow-y-auto">
            {recentMessages.length === 0 ? (
              <div className="px-4 py-8 text-center text-nature-500 dark:text-nature-400">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 text-nature-300 dark:text-nature-600" />
                <p className="text-sm">Inga olästa meddelanden</p>
              </div>
            ) : (
              <div className="divide-y divide-nature-100 dark:divide-nature-700">
                {recentMessages.map((message) => (
                  <div
                    key={message.id}
                    className="p-4 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                          <MessageSquare className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-nature-900 dark:text-nature-100 truncate">
                            {message.recipient_name || 'Okänd'}
                          </p>
                          <span className="text-xs text-nature-500 dark:text-nature-400">
                            {format(new Date(message.created_at), 'HH:mm', { locale: sv })}
                          </span>
                        </div>

                        <p className="text-sm text-nature-600 dark:text-nature-300 mt-1 line-clamp-2">
                          {message.message}
                        </p>

                        {/* Action buttons */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {message.assignment_id && (
                            <button
                              onClick={() => handleMessageClick(message, 'assignment')}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
                            >
                              <FileText className="h-3 w-3" />
                              Gå till uppdrag
                            </button>
                          )}
                          <button
                            onClick={() => handleMessageClick(message, 'recipient')}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                              message.recipient_type === 'customer'
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
                                : 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800'
                            }`}
                          >
                            {message.recipient_type === 'customer' ? (
                              <User className="h-3 w-3" />
                            ) : (
                              <Leaf className="h-3 w-3" />
                            )}
                            {message.recipient_type === 'customer' ? 'Gå till kund' : 'Gå till trädgårdsmästare'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {recentMessages.length > 0 && (
            <div className="px-4 py-2 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900">
              <button
                onClick={handleViewAll}
                className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 w-full text-center font-medium"
              >
                Öppna alla meddelanden
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
